import moment from "moment";
import { EditButton, Show, useRecordContext } from "react-admin";
import { Box, Chip, Divider, Grid, Link, Paper, Stack, Typography } from "@mui/material";
import CampaignIcon from "@mui/icons-material/Campaign";
import { CampaignPageResponse } from "../../interfaces/CampaignPage/CampaignPage";
import { CAMPAIGN_SITE_URL } from "./CampaignPageFormFields";

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <Grid item xs={12} sm={6}>
        <Typography variant="caption" color="text.secondary">
            {label}
        </Typography>
        <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
            {children}
        </Typography>
    </Grid>
);

const formatDate = (value?: string) => (value ? moment(value).format("DD MMM YYYY, hh:mm A") : "-");

const CampaignPageDetails = () => {
    const record = useRecordContext<CampaignPageResponse>();
    if (!record) return null;
    const pageUrl = `${CAMPAIGN_SITE_URL}/${record.slug}`;

    return (
        <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
            <Paper elevation={0} sx={{ p: 2.5, mb: 2, border: "1px solid #e8ebed", borderRadius: 2 }}>
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    alignItems={{ xs: "flex-start", md: "center" }}
                    justifyContent="space-between"
                    gap={2}
                >
                    <Stack direction="row" alignItems="center" gap={2}>
                        <CampaignIcon color="primary" />
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {record.campaignName}
                            </Typography>
                            <Link href={pageUrl} target="_blank" rel="noopener noreferrer" variant="body2">
                                {pageUrl}
                            </Link>
                        </Box>
                    </Stack>
                    <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                        <Chip label={record.service} size="small" />
                        <Chip
                            label={record.isActive ? "Active" : "Inactive (404)"}
                            size="small"
                            sx={{
                                backgroundColor: record.isActive ? "#d4edda" : "#f8d7da",
                                color: record.isActive ? "#155724" : "#721c24",
                                fontWeight: 500,
                            }}
                        />
                        <Chip
                            label={record.noindex ? "Hidden from search" : "Indexed"}
                            size="small"
                            sx={{ backgroundColor: record.noindex ? "#fff3cd" : "#d1ecf1", fontWeight: 500 }}
                        />
                        <EditButton />
                    </Stack>
                </Stack>
            </Paper>

            <Paper elevation={0} sx={{ p: 2.5, mb: 2, border: "1px solid #e8ebed", borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Banner
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                    <Field label="Heading">{record.bannerHeading}</Field>
                    <Field label="Description">{record.bannerDescription}</Field>
                    <Grid item xs={12}>
                        <Box
                            component="img"
                            src={record.bannerImageUrl}
                            alt="Banner"
                            sx={{ width: "100%", maxWidth: 640, borderRadius: "8px", border: "1px solid rgba(0,0,0,0.1)" }}
                        />
                    </Grid>
                </Grid>
            </Paper>

            <Paper elevation={0} sx={{ p: 2.5, mb: 2, border: "1px solid #e8ebed", borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    SEO
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                    <Field label="Meta Title">{record.metaTitle}</Field>
                    <Field label="Meta Description">{record.metaDescription}</Field>
                    <Field label="Created At">{formatDate(record.createdAt)}</Field>
                    <Field label="Updated At">{formatDate(record.updatedAt)}</Field>
                </Grid>
            </Paper>
        </Box>
    );
};

const CampaignPageShow = (props: any) => (
    <Show {...props} actions={false}>
        <CampaignPageDetails />
    </Show>
);

export default CampaignPageShow;
